import { useCallback, useEffect, useState } from "react";
import { Button, Drawer, Input, Spin, Table, Tag } from "antd";
import type { TableColumnsType } from "antd";
import { AdminListShell } from "../components/admin/AdminListShell";
import { fetchAdminCreditLedgerPage } from "../api/client";
import type { AdminCreditLedgerRow } from "../api/types";
import { fmtDateTime } from "../lib/formatAdmin";

export function CreditLedgerPage() {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(15);
  const [userId, setUserId] = useState("");
  const [items, setItems] = useState<AdminCreditLedgerRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<"forbidden" | "error" | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [active, setActive] = useState<AdminCreditLedgerRow | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setErr(null);
    const r = await fetchAdminCreditLedgerPage({
      page,
      page_size: pageSize,
      user_id: userId || undefined,
    });
    if (r === "forbidden") setErr("forbidden");
    else if (r === "error") setErr("error");
    else {
      setItems(r.items);
      setTotal(r.total);
    }
    setLoading(false);
  }, [page, pageSize, userId]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    setPage(1);
  }, [userId]);

  const columns: TableColumnsType<AdminCreditLedgerRow> = [
    {
      title: "用户",
      key: "user",
      width: 260,
      ellipsis: true,
      render: (_, row) => (
        <div>
          <div className="admin-ledger-email">{row.user_email ?? "—"}</div>
          <div
            className="admin-ledger-mono"
            title={row.user_id}
            style={{
              maxWidth: 240,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {row.user_id}
          </div>
        </div>
      ),
    },
    {
      title: "变动",
      dataIndex: "change_type",
      key: "ct",
      width: 108,
      render: (t: string) => <Tag>{t}</Tag>,
    },
    {
      title: "数量",
      dataIndex: "amount",
      key: "amt",
      width: 96,
      align: "right",
      render: (n: number) => (
        <span className="admin-ledger-num" style={{ color: n < 0 ? "#9e3f4e" : undefined }}>
          {n > 0 ? `+${n}` : String(n)}
        </span>
      ),
    },
    {
      title: "结余",
      dataIndex: "balance_after",
      key: "bal",
      width: 96,
      align: "right",
      render: (n: number) => <span className="admin-ledger-num admin-ledger-num--sub">{n}</span>,
    },
    {
      title: "时间",
      dataIndex: "created_at",
      key: "t",
      width: 172,
      render: fmtDateTime,
    },
    {
      title: "操作",
      key: "op",
      width: 88,
      align: "center",
      fixed: "right",
      render: (_, row) => (
        <Button
          type="link"
          size="small"
          onClick={() => {
            setActive(row);
            setDrawerOpen(true);
          }}
        >
          详情
        </Button>
      ),
    },
  ];

  return (
    <>
      <AdminListShell
        title="额度流水"
        description="扫描点数增减与结余（只读，单位：点）。列表仅保留主字段，来源与备注见详情抽屉。"
        err={err}
        errorBanner
        toolbar={
          <div className="admin-ledger-toolbar">
            <Input
              placeholder="用户 UUID 过滤"
              value={userId}
              onChange={(e) => setUserId(e.target.value)}
              variant="borderless"
              style={{ width: 260 }}
              allowClear
            />
          </div>
        }
        table={(bodyY) => (
        <div className="admin-ledger-table-wrap admin-ledger-table-wrap--flex">
          <Spin spinning={loading}>
            <Table<AdminCreditLedgerRow>
              rowKey="id"
              className="admin-ledger-table"
              columns={columns}
              dataSource={items}
              pagination={{
                current: page,
                pageSize,
                total,
                showSizeChanger: true,
                pageSizeOptions: [10, 15, 20, 50],
                showTotal: (t) => `共 ${t} 条`,
                onChange: (p, ps) => {
                  setPage(p);
                  setPageSize(ps);
                },
              }}
              scroll={{ y: bodyY }}
              locale={{ emptyText: "暂无流水" }}
            />
          </Spin>
        </div>
      )}
    />

      <Drawer
        title={null}
        placement="right"
        size="38%"
        open={drawerOpen}
        onClose={() => {
          setDrawerOpen(false);
          setActive(null);
        }}
        destroyOnHidden
        className="admin-drawer"
        closable
      >
        {active ? (
          <>
            <div className="admin-drawer-kicker">CREDIT LEDGER</div>
            <div className="admin-drawer-title-row">
              <Tag>{active.change_type}</Tag>
              <h2 className="admin-drawer-title">
                {active.amount > 0 ? "+" : ""}
                {active.amount} 点
              </h2>
            </div>
            <p className="admin-ledger-mono" style={{ marginTop: 8 }}>
              流水 ID · {active.id}
            </p>

            <section className="admin-drawer-section">
              <h3 className="admin-drawer-section-title">用户</h3>
              <div className="admin-drawer-card">
                <div className="val" style={{ fontWeight: 600 }}>
                  {active.user_email ?? "—"}
                </div>
                <div className="admin-ledger-mono" style={{ marginTop: 8, wordBreak: "break-all" }}>
                  {active.user_id}
                </div>
              </div>
            </section>

            <section className="admin-drawer-section">
              <h3 className="admin-drawer-section-title">账务</h3>
              <div className="admin-drawer-kv">
                <div className="admin-drawer-kv-item">
                  <label>变动数量</label>
                  <div className="val val--accent">
                    {active.amount > 0 ? "+" : ""}
                    {active.amount} 点
                  </div>
                </div>
                <div className="admin-drawer-kv-item">
                  <label>变动后结余</label>
                  <div className="val">{active.balance_after} 点</div>
                </div>
                <div className="admin-drawer-kv-item">
                  <label>发生时间</label>
                  <div className="val">{fmtDateTime(active.created_at)}</div>
                </div>
              </div>
            </section>

            <section className="admin-drawer-section">
              <h3 className="admin-drawer-section-title">来源</h3>
              <div className="admin-drawer-kv">
                <div className="admin-drawer-kv-item">
                  <label>来源类型</label>
                  <div className="val">{active.source_type}</div>
                </div>
                <div className="admin-drawer-kv-item">
                  <label>来源 ID</label>
                  <div className="val admin-ledger-mono" style={{ wordBreak: "break-all" }}>
                    {active.source_id ?? "—"}
                  </div>
                </div>
              </div>
            </section>

            <section className="admin-drawer-section">
              <h3 className="admin-drawer-section-title">备注</h3>
              <div className="admin-drawer-card">
                <div className="val" style={{ whiteSpace: "pre-wrap" }}>
                  {active.remark?.trim() || "—"}
                </div>
              </div>
            </section>

            <div className="admin-drawer-footer">
              <button
                type="button"
                className="admin-btn-primary-ledger"
                onClick={() => {
                  setDrawerOpen(false);
                  setActive(null);
                }}
              >
                关闭
              </button>
            </div>
          </>
        ) : null}
      </Drawer>
    </>
  );
}
